import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { UploadCloud, RefreshCw, Save } from "lucide-react";
import ChangePasswordSection from "./ChangePasswordSection";
import AllowedCountriesSection from "./AllowedCountriesSection";
import SenderSection from "./SenderSection";

const FIELDS = [
  { key: "brand_name", label: "Brand name", placeholder: "Verifine Wines" },
  { key: "tagline", label: "Tagline (admin login)", placeholder: "Curate the cellar. Verify the bottle.", textarea: true },
  { key: "portal_overline", label: "Portal overline", placeholder: "Private Reserve" },
  { key: "portal_heading", label: "Portal heading", placeholder: "Enter your code" },
  { key: "portal_subtitle", label: "Portal subtitle", placeholder: "Enter the code that came with your bottle…", textarea: true },
  { key: "portal_footer", label: "Portal footer", placeholder: "Verified • Authentic • Confidential" },
  { key: "public_url", label: "Public URL (NFC links & certificates)", placeholder: "https://verifinewines.com", hint: "Leave blank to use the current browser origin. Set this so NFC links and certificate links point at the production domain." },
  { key: "accent_color", label: "Accent color (hex)", placeholder: "#C5A059", hint: "Used for the email template and code chips." },
];

export default function AdminSettings() {
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { setForm(settings); }, [settings]);

  const update = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        brand_name: form.brand_name,
        tagline: form.tagline,
        portal_overline: form.portal_overline,
        portal_heading: form.portal_heading,
        portal_subtitle: form.portal_subtitle,
        portal_footer: form.portal_footer,
        public_url: form.public_url || "",
        accent_color: form.accent_color || "#C5A059",
      });
      toast.success("Settings saved");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo too large (max 2MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const resetLogo = async () => {
    if (!window.confirm("Restore default logo?")) return;
    try {
      await api.put("/admin/settings", { logo_url: "https://customer-assets.emergentagent.com/job_secure-access-167/artifacts/mmleicge_Logo%20com%20qualidade.png" });
      toast.success("Logo reset");
      refresh();
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div data-testid="admin-settings" className="space-y-10 max-w-3xl">
      <header>
        <p className="overline text-[var(--vault-text-muted)] mb-2">Cellar</p>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Settings</h1>
        <p className="text-[var(--vault-text-muted)] mt-3">
          Brand name, copy, logo and the public URL used in QR codes. Changes apply across portal, admin and email notifications.
        </p>
      </header>

      {/* Logo */}
      <section
        className="rounded-lg p-8 border"
        style={{ background: "var(--vault-surface)", borderColor: "var(--vault-border)" }}
      >
        <h2 className="font-display text-2xl mb-6">Logo</h2>
        <div className="flex items-center gap-8 flex-wrap">
          <div
            className="w-32 h-32 rounded-lg flex items-center justify-center"
            style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }}
          >
            <img
              src={settings.logo_url}
              alt="Current logo"
              className="max-w-full max-h-full object-contain"
              style={{ mixBlendMode: "lighten" }}
              data-testid="settings-logo-preview"
            />
          </div>
          <div className="flex-1 min-w-[260px] space-y-3">
            <p className="text-sm text-[var(--vault-text-muted)]">PNG, JPG or SVG with transparent background works best. Max 2MB.</p>
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="settings-upload-logo"
                className="btn-gold inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm"
              >
                {uploading ? <span className="spin" /> : <UploadCloud className="w-4 h-4" />}
                Upload new logo
              </button>
              <button
                type="button"
                onClick={resetLogo}
                className="btn-ghost inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm"
              >
                <RefreshCw className="w-4 h-4" />
                Reset to default
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={uploadLogo}
              className="hidden"
              data-testid="settings-logo-input"
            />
          </div>
        </div>
      </section>

      {/* Text fields */}
      <form
        onSubmit={save}
        className="rounded-lg p-8 border space-y-5"
        style={{ background: "var(--vault-surface)", borderColor: "var(--vault-border)" }}
      >
        <h2 className="font-display text-2xl mb-2">Brand & copy</h2>
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="overline block text-[var(--vault-text-muted)] mb-2">{f.label}</label>
            {f.textarea ? (
              <textarea
                value={form[f.key] || ""}
                onChange={(e) => update(f.key, e.target.value)}
                placeholder={f.placeholder}
                rows={2}
                data-testid={`settings-${f.key}`}
                className="w-full px-3 py-2.5 rounded outline-none text-sm resize-y"
                style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }}
              />
            ) : (
              <input
                type="text"
                value={form[f.key] || ""}
                onChange={(e) => update(f.key, e.target.value)}
                placeholder={f.placeholder}
                data-testid={`settings-${f.key}`}
                className="w-full px-3 py-2.5 rounded outline-none text-sm"
                style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }}
              />
            )}
            {f.hint && <p className="text-[10px] text-[var(--vault-text-faint)] mt-1">{f.hint}</p>}
          </div>
        ))}

        <div className="flex justify-end gap-2 pt-4 border-t" style={{ borderColor: "var(--vault-border)" }}>
          <button
            type="submit"
            disabled={saving}
            data-testid="settings-save"
            className="btn-gold inline-flex items-center gap-2 px-6 py-2.5 rounded-md text-sm"
          >
            {saving ? <span className="spin" /> : <Save className="w-4 h-4" />}
            Save settings
          </button>
        </div>
      </form>

      <SenderSection />

      <AllowedCountriesSection />

      <ChangePasswordSection />
    </div>
  );
}
